import React, { useState, useEffect, useMemo } from 'react';
import { ShieldAlert, Activity, Loader2, Target, Folder, Sparkles } from 'lucide-react';
import { Modal } from '../Modal';
import { IncaricoService } from '../../services/apiService';
import { useToast } from '../ToastContext';

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

type Mode = 'EXISTING' | 'NEW';

// Soggetti dichiarati in pratica.datiAggiuntiviJson ([] se assenti).
const extractSoggetti = (pratica: any): { nome: string }[] => {
    if (!pratica) return [];
    try {
        const dati = typeof pratica.datiAggiuntiviJson === 'string'
            ? JSON.parse(pratica.datiAggiuntiviJson)
            : pratica.datiAggiuntiviJson || {};
        if (Array.isArray(dati.soggetti) && dati.soggetti.length > 0) return dati.soggetti;
        if (dati.soggetto_nome || dati.soggetto_denominazione) {
            return [{ nome: dati.soggetto_nome || dati.soggetto_denominazione }];
        }
    } catch { /* ignore */ }
    return [];
};

export const UrgenzaModal: React.FC<Props> = ({ onClose, onCreated }) => {
    const { addToast } = useToast();
    const [praticheAutorizzate, setPraticheAutorizzate] = useState<any[]>([]);
    const [praticaId, setPraticaId] = useState('');
    const [soggettoNome, setSoggettoNome] = useState('');
    const [noteUrgenza, setNoteUrgenza] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [loadingPratiche, setLoadingPratiche] = useState(true);
    // "Senza mandato": basta un nome libero; il titolare abbinerà poi l'incarico
    // a una pratica dal tab "Da Abbinare".
    const [mode, setMode] = useState<Mode>('EXISTING');
    const [nomeLibero, setNomeLibero] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await IncaricoService.getPraticheAutorizzate();
                setPraticheAutorizzate(res.data || []);
            } catch {
                addToast('Impossibile caricare le pratiche autorizzate.', 'error');
            } finally {
                setLoadingPratiche(false);
            }
        })();
    }, []);

    const praticaSelezionata = useMemo(
        () => praticheAutorizzate.find(p => String(p.id) === praticaId),
        [praticheAutorizzate, praticaId],
    );
    const soggettiList = useMemo(() => extractSoggetti(praticaSelezionata), [praticaSelezionata]);

    // Al cambio pratica si azzera il soggetto; se ce n'è uno solo si preseleziona.
    useEffect(() => {
        if (soggettiList.length === 1) setSoggettoNome(soggettiList[0].nome);
        else setSoggettoNome('');
    }, [praticaId, soggettiList]);

    const requiresSoggetto = soggettiList.length >= 2;
    const missingSoggetto = requiresSoggetto && !soggettoNome;
    const canConfirm = mode === 'EXISTING'
        ? (!!praticaId && !missingSoggetto)
        : nomeLibero.trim().length >= 3;

    const handleCreate = async () => {
        setIsCreating(true);
        try {
            if (mode === 'NEW') {
                // Senza mandato: incarico da abbinare in seguito dal titolare.
                if (nomeLibero.trim().length < 3) {
                    addToast('Inserisci un nome per l\'incarico (min. 3 caratteri)', 'error');
                    return;
                }
                await IncaricoService.createUrgenteSenzaMandato(nomeLibero.trim());
                addToast('Incarico avviato — verrà abbinato a fine turno dal titolare', 'success');
            } else {
                if (!praticaId) { addToast('Seleziona una pratica valida', 'error'); return; }
                if (missingSoggetto) { addToast('Seleziona il soggetto su cui avviare l\'incarico urgente', 'error'); return; }
                // urgente-da-pratica crea un incarico URGENTE visibile in /me;
                // /rapido creerebbe un AUTOGENERATO, nascosto dall'Agenda Operativa.
                await IncaricoService.createUrgenteDaPratica(
                    Number(praticaId),
                    noteUrgenza,
                    soggettoNome || undefined,
                );
                addToast('Incarico di urgenza avviato!', 'success');
            }
            onCreated();
        } catch {
            addToast("Errore nell'avvio dell'incarico", 'error');
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Modal title="Avvia Incarico di Urgenza" onClose={() => !isCreating && onClose()}>
            <div className="space-y-5">
                <div className="bg-rose-50 text-rose-800 p-3 rounded-lg text-xs font-medium border border-rose-200 flex items-start gap-2">
                    <ShieldAlert size={16} className="shrink-0 mt-0.5" />
                    <p>Usa questa funzione solo per <strong>avvii d'emergenza concordati con la sede</strong>. Puoi partire da un mandato esistente o, se il cliente non è ancora a sistema, aprire un incarico senza mandato che il titolare abbinerà a fine turno.</p>
                </div>

                {/* Scelta tra mandato esistente e "senza mandato" (solo nome libero) */}
                <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl">
                    <button
                        type="button"
                        onClick={() => setMode('EXISTING')}
                        disabled={isCreating}
                        className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-colors ${
                            mode === 'EXISTING'
                                ? 'bg-white text-rose-700 shadow-sm border border-rose-200'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        <Folder size={14} /> Da mandato esistente
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('NEW')}
                        disabled={isCreating}
                        className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-colors ${
                            mode === 'NEW'
                                ? 'bg-white text-amber-700 shadow-sm border border-amber-200'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        Senza mandato (da abbinare)
                    </button>
                </div>

                {mode === 'NEW' ? (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg text-xs flex items-start gap-2">
                            <p>
                                Dai un nome breve all'incarico (es. <em>"Cliente Rossi sera"</em>).
                                Lavora normalmente — il titolare lo collegherà al mandato corretto a fine turno.
                            </p>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Nome incarico *</label>
                            <input
                                type="text"
                                placeholder="Es. Cliente Rossi sera"
                                className="w-full p-3 mt-1 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 ring-amber-400 font-semibold text-slate-800"
                                value={nomeLibero}
                                onChange={e => setNomeLibero(e.target.value)}
                                disabled={isCreating}
                                maxLength={120}
                                autoFocus
                            />
                        </div>
                    </div>
                ) : loadingPratiche ? (
                    <div className="text-center p-6 border border-dashed border-slate-200 rounded-xl text-slate-500 text-sm">
                        <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                        Verifica autorizzazioni in corso...
                    </div>
                ) : (
                    <>
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Seleziona Fascicolo / Mandato *</label>
                            <select
                                className="w-full p-3 mt-1 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 ring-rose-500 font-semibold text-slate-800"
                                value={praticaId}
                                onChange={e => setPraticaId(e.target.value)}
                                disabled={isCreating}
                            >
                                <option value="">-- Scegli da storico --</option>
                                {praticheAutorizzate.map(p => {
                                    // Il riferimento del mandato se c'è, altrimenti "Mandato #id": al
                                    // collaboratore non si mostrano né il cliente né il soggetto.
                                    const label = p.riferimento?.trim() || `Mandato #${p.id}`;
                                    return <option key={p.id} value={p.id}>{label}</option>;
                                })}
                            </select>
                        </div>

                        {/* Soggetto investigato: si chiede solo se il mandato ne ha più di uno */}
                        {praticaSelezionata && soggettiList.length >= 2 && (
                            <div>
                                <label className="text-[10px] font-bold text-orange-700 uppercase flex items-center gap-1">
                                    <Target size={12}/> Soggetto da seguire *
                                </label>
                                <select
                                    className={`w-full p-3 mt-1 bg-orange-50 border rounded-lg outline-none focus:ring-2 ring-orange-400 font-semibold text-slate-800 ${missingSoggetto ? 'border-red-400 ring-1 ring-red-300' : 'border-orange-200'}`}
                                    value={soggettoNome}
                                    onChange={e => setSoggettoNome(e.target.value)}
                                    disabled={isCreating}
                                >
                                    <option value="">-- Seleziona soggetto --</option>
                                    {soggettiList.map((s, idx) => (
                                        <option key={idx} value={s.nome}>{s.nome}</option>
                                    ))}
                                </select>
                                {missingSoggetto && (
                                    <p className="text-xs text-red-600 mt-1">
                                        Il mandato ha più soggetti: scegli quello su cui stai per intervenire.
                                    </p>
                                )}
                            </div>
                        )}

                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Note per la Sede (Opzionale)</label>
                            <input
                                type="text"
                                placeholder="Es. Chiamato da Mario per target avvistato al bar..."
                                className="w-full p-3 mt-1 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 ring-rose-500 text-sm"
                                value={noteUrgenza}
                                onChange={e => setNoteUrgenza(e.target.value)}
                                disabled={isCreating}
                            />
                        </div>
                    </>
                )}

                <div className="flex gap-2 pt-2">
                    <button onClick={onClose} disabled={isCreating} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-100 rounded-xl transition-colors">Annulla</button>
                    <button
                        onClick={handleCreate}
                        disabled={isCreating || !canConfirm}
                        className="flex-[2] flex items-center justify-center gap-2 py-3 bg-rose-600 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md active:scale-95"
                    >
                        {isCreating ? <Loader2 size={18} className="animate-spin" /> : <Activity size={18} />}
                        Avvia Subito
                    </button>
                </div>
            </div>
        </Modal>
    );
};
